/*
 * Bottom-left banners that come out of the Agents button. One arrival shows
 * one card; several collapse into a summary. A card stays 8s (paused while
 * hovered or the tab is hidden), then is pulled back into the button, which
 * bounces. The action column has the main action and Snooze below it; there
 * is no close button. Opening the Agents menu clears the banners.
 */
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { OPEN_THREAD_STATE } from "@/features/agents/routes";
import { runningTurnsStore } from "@/features/agents/runningTurnsStore";
import { cn } from "@/lib/utils";

import { announceKey, createAnnouncer, lapseDelay } from "../bannerQueue";
import {
  isSnoozed,
  itemRoute,
  useAgentAttention
} from "../context/AgentAttentionProvider";
import { useAgentsTrayStore } from "../context/agentsTrayStore";
import { useDocumentHidden } from "../hooks/useAttentionDocumentSignals";
import { itemSeed } from "../faces";
import {
  byUrgency,
  groupTone,
  SIGNAL_META,
  signalClock,
  signalFace,
  signalTone,
  TONE_CLASSES
} from "../signalTone";
import type { AttentionItem } from "../types";
import { GLASS } from "./AgentsMenu";
import { FaceStack } from "./FaceStack";
import { itemName, LabelLine, SignalBody } from "./SignalBody";
import { SignalFace } from "./SignalFace";

export const BANNER_MS = 8_000;
export const GENIE_OUT_MS = 420;

const ActionColumn: FC<{
  label: string;
  toneText: string;
  onMain: () => void;
  onSnooze: () => void;
}> = ({ label, toneText, onMain, onSnooze }) => (
  <div className="flex w-18 shrink-0 flex-col border-l border-slate-900/[0.07] dark:border-white/[0.07]">
    <button
      type="button"
      onClick={onMain}
      className={cn(
        "flex flex-1 items-center justify-center text-base font-semibold transition-colors hover:bg-fill-1",
        toneText
      )}
    >
      {label}
    </button>
    <button
      type="button"
      onClick={onSnooze}
      className="h-7 border-t border-slate-900/[0.07] text-xs text-muted-foreground transition-colors hover:bg-fill-1 hover:text-foreground dark:border-white/[0.07]"
    >
      Snooze
    </button>
  </div>
);

export const SignalAlerts: FC = () => {
  const navigate = useNavigate();
  const { items, loaded, snooze } = useAgentAttention();
  const trayOpen = useAgentsTrayStore((s) => s.open);
  const sheetOpen = useAgentsTrayStore((s) => s.sheetOpen);
  const setOpen = useAgentsTrayStore((s) => s.setOpen);
  const bumpGulp = useAgentsTrayStore((s) => s.bumpGulp);
  const hidden = useDocumentHidden();

  const announcer = useRef(createAnnouncer()).current;
  const [fresh, setFresh] = useState<AttentionItem[]>([]);
  const [leaving, setLeaving] = useState<AttentionItem[] | null>(null);
  const [hovered, setHovered] = useState(false);
  const [lapseTick, setLapseTick] = useState(0);
  const quiet = trayOpen || sheetOpen;

  useEffect(() => {
    if (!loaded) return undefined;
    const now = Date.now();
    const arrivals = announcer
      .evaluate(items, now)
      .filter((i) => !runningTurnsStore.isViewed(i.thread_id));
    const current = new Map(items.map((i) => [announceKey(i), i]));
    setFresh((cur) => {
      const kept = cur
        .map((i) => current.get(announceKey(i)))
        .filter((i): i is AttentionItem => !!i && !isSnoozed(i, now));
      const keys = new Set(kept.map(announceKey));
      const added = quiet
        ? []
        : arrivals.filter((i) => !keys.has(announceKey(i)));
      return added.length || kept.length !== cur.length
        ? [...kept, ...added]
        : cur;
    });
    const next = announcer.nextLapse(items, now);
    if (next === null) return undefined;
    const t = setTimeout(
      () => setLapseTick((n) => n + 1),
      lapseDelay(next, now)
    );
    return () => clearTimeout(t);
  }, [items, loaded, lapseTick, announcer, quiet]);

  useEffect(() => {
    if (quiet) {
      setFresh([]);
      setLeaving(null);
    }
  }, [quiet]);

  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leave = useCallback(
    (cards: AttentionItem[]) => {
      if (!cards.length) return;
      setLeaving(cards);
      const gone = new Set(cards.map(announceKey));
      setFresh((cur) => cur.filter((i) => !gone.has(announceKey(i))));
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
      leaveTimer.current = setTimeout(() => {
        leaveTimer.current = null;
        setLeaving(null);
        bumpGulp();
      }, GENIE_OUT_MS);
    },
    [bumpGulp]
  );
  useEffect(
    () => () => {
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    },
    []
  );

  const cardKey = fresh.map(announceKey).join("|");
  useEffect(() => {
    if (!fresh.length || hovered || hidden) return undefined;
    const t = setTimeout(() => leave(fresh), BANNER_MS);
    return () => clearTimeout(t);
    // The 8s restart on a new arrival (cardKey), not on item field updates.
  }, [cardKey, hovered, hidden, leave]);

  const onSnooze = async (cards: AttentionItem[]) => {
    leave(cards);
    const until = await snooze(cards);
    if (until)
      toast(`Snoozed until ${signalClock(until)}. It's still in Agents.`);
  };

  const onOpen = (item: AttentionItem) => {
    setFresh([]);
    navigate(itemRoute(item), { state: OPEN_THREAD_STATE });
  };

  const showing = leaving && !fresh.length ? leaving : fresh;
  if (!showing.length) return null;
  const isLeaving = showing === leaving;
  const sorted = byUrgency(showing);
  const lead = sorted[0];
  const tone = TONE_CLASSES[groupTone(sorted) ?? "green"];

  return (
    <div
      key={isLeaving ? "out" : cardKey}
      role="status"
      aria-live="polite"
      data-testid="signal-alert"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={cn(
        GLASS,
        "flex w-90 overflow-hidden",
        isLeaving ? "signal-genie-out" : "signal-genie-in"
      )}
    >
      {sorted.length === 1 ? (
        <>
          <div className="flex min-w-0 flex-1 items-start gap-2.5 p-3">
            <SignalFace seed={itemSeed(lead)} item={lead} />
            <SignalBody item={lead} />
          </div>
          <ActionColumn
            label={SIGNAL_META[signalFace(lead)].action}
            toneText={TONE_CLASSES[signalTone(lead)].text}
            onMain={() => onOpen(lead)}
            onSnooze={() => void onSnooze(sorted)}
          />
        </>
      ) : (
        <>
          <div
            className="flex min-w-0 flex-1 items-start gap-2.5 p-3"
            data-testid="signal-alert-summary"
          >
            <FaceStack
              seeds={sorted.map(itemSeed)}
              size="banner"
              ringClassName="ring-white/80 dark:ring-zinc-900/80"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="truncate text-base leading-4.5 font-semibold text-foreground">
                {groupTone(sorted) === "red"
                  ? `${sorted.length} agents need you`
                  : `${sorted.length} agents finished`}
              </div>
              <LabelLine
                label={SIGNAL_META[signalFace(lead)].label}
                time={signalClock(lead.since)}
                className={TONE_CLASSES[signalTone(lead)].text}
              />
              <p className="line-clamp-1 text-xs leading-4 text-fg-2">
                {Array.from(new Set(sorted.map(itemName))).join(", ")}
              </p>
            </div>
          </div>
          <ActionColumn
            label="View"
            toneText={tone.text}
            onMain={() => {
              setFresh([]);
              setOpen(true);
            }}
            onSnooze={() => void onSnooze(sorted)}
          />
        </>
      )}
    </div>
  );
};
